/**
 * BaseRepository - Foundation class for all repositories
 * Provides common CRUD operations, error handling, and pagination support
 *
 * All concrete repositories should extend this class to ensure consistent
 * data access patterns across the application.
 *
 * User-Scoped Repositories (Client, Expense, Job, Quote, Invoice, Service):
 * - Override findAll and create with userId as first parameter
 * - All data is automatically scoped to the authenticated user
 *
 * Admin Repository (Admin):
 * - Uses base class signature without userId parameter
 * - Admin users can access all data
 */

import { PostgrestError } from '@supabase/supabase-js';

/**
 * Standard repository result type for operations that can fail
 */
export type RepositoryResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
}

/**
 * Query options for filtering and sorting
 */
export interface QueryOptions {
  orderBy?: string;
  ascending?: boolean;
  filters?: Record<string, any>;
  select?: string;
}

/**
 * Paginated result structure
 */
export interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Base repository class with common database operations
 *
 * NOTE: The base signature is findAll(options) and create(data)
 * User-scoped repositories override these with userId as first parameter:
 * - findAll(userId, options)
 * - create(userId, data)
 */
export abstract class BaseRepository<T> {
  /**
   * Abstract table name - must be defined by child classes
   */
  protected abstract tableName: string;

  /**
   * Abstract primary key field name (default: 'id')
   */
  protected primaryKey: string = 'id';

  /**
   * Default pagination limit
   */
  protected defaultLimit: number = 20;

  /**
   * Maximum pagination limit
   */
  protected maxLimit: number = 100;

  /**
   * Handle Supabase errors and convert to user-friendly messages
   */
  protected handleError(error: PostgrestError | Error | unknown, operation: string): string {
    if (error && typeof error === 'object' && 'message' in error) {
      const pgError = error as PostgrestError;
      if ('code' in pgError) {
        // Handle specific PostgreSQL error codes
        switch (pgError.code) {
          case '23505':
            return `A record with these details already exists.`;
          case '23503':
            return `This record cannot be deleted because it is referenced by other records.`;
          case '23502':
            return `Required field is missing.`;
          case '42501':
            return `You do not have permission to perform this action.`;
          case 'PGRST116':
            return `The requested resource was not found.`;
          default:
            return pgError.message || `An error occurred while ${operation}.`;
        }
      }
      return (error as Error).message || `An error occurred while ${operation}.`;
    }
    return `An unexpected error occurred while ${operation}.`;
  }

  /**
   * Build pagination metadata
   */
  protected buildPagination(
    page: number,
    limit: number,
    total: number
  ): PaginatedResult<T>['pagination'] {
    const totalPages = Math.ceil(total / limit);
    return {
      page,
      limit,
      total,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1,
    };
  }

  /**
   * Parse and validate pagination parameters
   */
  protected parsePagination(params?: PaginationParams): { page: number; limit: number } {
    const page = Math.max(1, params?.page || 1);
    const limit = Math.min(
      this.maxLimit,
      Math.max(1, params?.limit || this.defaultLimit)
    );
    return { page, limit };
  }

  /**
   * Calculate range for pagination
   */
  protected getRange(page: number, limit: number): { from: number; to: number } {
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    return { from, to };
  }

  /**
   * Apply query filters to a Supabase query builder
   */
  protected applyFilters(
    query: any,
    filters?: Record<string, any>
  ): any {
    if (!filters || Object.keys(filters).length === 0) {
      return query;
    }

    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      // Handle special operators
      if (typeof value === 'object') {
        if ('in' in value) {
          query = query.in(key, value.in);
        } else if ('like' in value) {
          query = query.like(key, value.like);
        } else if ('ilike' in value) {
          query = query.ilike(key, value.ilike);
        } else if ('gt' in value) {
          query = query.gt(key, value.gt);
        } else if ('gte' in value) {
          query = query.gte(key, value.gte);
        } else if ('lt' in value) {
          query = query.lt(key, value.lt);
        } else if ('lte' in value) {
          query = query.lte(key, value.lte);
        } else if ('neq' in value) {
          query = query.neq(key, value.neq);
        }
      } else {
        query = query.eq(key, value);
      }
    }

    return query;
  }

  /**
   * Find all records with pagination, filtering, and sorting
   *
   * User-scoped repositories: findAll(userId, options)
   * Admin repository: findAll(options)
   *
   * Must be implemented by concrete repositories
   */
  abstract findAll(...args: any[]): Promise<RepositoryResult<any>>;

  /**
   * Find a single record by ID
   * Must be implemented by concrete repositories
   */
  abstract findById(id: string): Promise<RepositoryResult<T>>;

  /**
   * Create a new record
   *
   * User-scoped repositories: create(userId, data)
   * Admin repository: create(data)
   *
   * Must be implemented by concrete repositories
   */
  abstract create(...args: any[]): Promise<RepositoryResult<T>>;

  /**
   * Update an existing record
   * Must be implemented by concrete repositories
   */
  abstract update(id: string, data: Partial<T>): Promise<RepositoryResult<T>>;

  /**
   * Delete a record
   * Must be implemented by concrete repositories
   */
  abstract delete(id: string): Promise<RepositoryResult<void>>;

  /**
   * Check if a record has dependencies (foreign key relationships)
   * Useful for preventing deletion of records that are referenced elsewhere
   */
  abstract hasDependencies(id: string): Promise<RepositoryResult<boolean>>;
}
